import {
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@repo/ui";
import { useState } from "react";
import {
  Calendar,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Clock,
  FileText,
  User,
  XCircle,
} from "lucide-react";
import {
  adapterReponsesEgogramme,
  calculerScoresEgogramme,
  formatDateTime,
  formatDuration,
  ordonnerGroupesEgogramme,
} from "@repo/utils";
import { EgogrammeChart } from "./EgogrammeChart";
import {
  couleurFamille,
  couleurProfilAtomique,
  couleurSousGroupe,
} from "./egogrammePalette";

interface FormReponseEgogramme {
  name: string;
  label: string;
  profil: string;
  response?: boolean;
}

interface EgogrammeDonnees {
  type: "form";
  template: "egogramme";
  reponses: Record<string, FormReponseEgogramme>;
}

interface CandidatInfo {
  prenom: string;
  nom: string;
  email: string;
  dateDebut?: number;
  dateFin?: number;
  tempsPasse?: number;
}

interface EgogrammeResultsProps {
  donnees: EgogrammeDonnees;
  candidatInfo: CandidatInfo;
}

export function EgogrammeResults({ donnees, candidatInfo }: EgogrammeResultsProps) {
  // Repliée par défaut : les 140 lignes de réponses sont une pièce
  // justificative, pas la lecture du test. Ce qui se lit d'abord, ce sont les
  // scores par état du moi.
  const [brutOpen, setBrutOpen] = useState(false);

  if (donnees.type !== "form" || donnees.template !== "egogramme") {
    return <div>Type de données incorrect</div>;
  }

  const reponsesList = Object.entries(donnees.reponses);

  // Scores agrégés par état du moi. Somme brute de 0/1 : le ratio affiché est
  // décidé ici, la couche de calcul ne normalise rien.
  const { resultats, invalides } = calculerScoresEgogramme(
    adapterReponsesEgogramme(donnees.reponses),
  );
  // Ordre CANONIQUE de l'égogramme (Parent → Adulte → Enfant), partagé avec
  // l'histogramme : une barre et une carte au même rang désignent le même état.
  // Il ne dépend ni des scores ni de l'alphabet.
  const groupes = ordonnerGroupesEgogramme(resultats);

  const profilCounts: Record<string, { oui: number; non: number }> = {};
  reponsesList.forEach(([, reponse]) => {
    if (!profilCounts[reponse.profil]) {
      profilCounts[reponse.profil] = { oui: 0, non: 0 };
    }
    if (reponse.response) {
      profilCounts[reponse.profil]!.oui++;
    } else {
      profilCounts[reponse.profil]!.non++;
    }
  });

  return (
    <div>
      {/* Header */}
      <div className="mb-6 p-4 md:p-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold">Egogramme</h1>
            <div className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
              <User className="h-4 w-4" />
              {candidatInfo.prenom} {candidatInfo.nom} —{" "}
              {candidatInfo.email}
            </div>
          </div>
          <Badge variant="success">Terminée</Badge>
        </div>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          {candidatInfo.tempsPasse !== undefined && (
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <div>
                <span className="text-muted-foreground">Temps passé</span>
                <p className="font-medium">
                  {formatDuration(candidatInfo.tempsPasse)}
                </p>
              </div>
            </div>
          )}
          {candidatInfo.dateDebut && (
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <div>
                <span className="text-muted-foreground">Date de début</span>
                <p className="font-medium">
                  {formatDateTime(candidatInfo.dateDebut)}
                </p>
              </div>
            </div>
          )}
          {candidatInfo.dateFin && (
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <div>
                <span className="text-muted-foreground">Date de fin</span>
                <p className="font-medium">
                  {formatDateTime(candidatInfo.dateFin)}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Card Scores par état du moi */}
      <Card className="mb-6 w-full overflow-hidden">
        <CardHeader>
          <CardTitle>Scores par état du moi</CardTitle>
          <CardDescription>
            Hauteur des barres = taux d'accord ; les chiffres sont les scores
            bruts, sur le nombre de questions répondues
            {invalides > 0 && ` — ${invalides} réponse(s) non interprétable(s)`}
          </CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <EgogrammeChart groupes={groupes} />
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {groupes.map(([nom, groupe]) => (
              <div
                key={nom}
                // Teinte de famille en fond, texte en encre : la couleur
                // rappelle la barre correspondante, le nom porte l'information.
                className="p-4 rounded-surface border border-trait text-encre"
                style={{ backgroundColor: couleurFamille(nom) }}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <p className="text-sm font-semibold">{nom}</p>
                  <p className="text-lg font-bold tabular-nums">
                    {groupe.score}
                    <span className="text-sm font-normal opacity-70">
                      {" / "}
                      {groupe.total}
                    </span>
                  </p>
                </div>
                {groupe.sousGroupes && (
                  <ul className="mt-3 space-y-1 text-sm">
                    {Object.entries(groupe.sousGroupes).map(([sousNom, s]) => (
                      <li key={sousNom} className="flex justify-between gap-2">
                        <span className="flex items-center gap-2">
                          <span
                            className="h-2.5 w-2.5 shrink-0 rounded-sm border border-black/10"
                            style={{
                              backgroundColor: couleurSousGroupe(nom, sousNom),
                            }}
                          />
                          {sousNom}
                        </span>
                        <span className="tabular-nums font-medium">
                          {s.score} / {s.total}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Card Résultats bruts — repliée par défaut */}
      <Card className="mb-6 w-full overflow-hidden">
        <CardHeader className="p-0">
          <button
            type="button"
            // Un bouton et non un `div` cliquable : le repli doit s'atteindre au
            // clavier et s'annoncer aux lecteurs d'écran.
            onClick={() => setBrutOpen((ouvert) => !ouvert)}
            aria-expanded={brutOpen}
            aria-controls="egogramme-resultats-bruts"
            // Contour de focus intérieur, arrondi comme la carte : le bouton en
            // occupe toute la largeur, et `overflow-hidden` rognait un contour
            // extérieur.
            className="flex w-full cursor-pointer select-none items-center justify-between gap-4 rounded-surface p-6 text-left focus-visible:-outline-offset-2"
          >
            {/* `CardTitle` / `CardDescription` rendent des `div` : dans un
                `button`, dont le contenu doit rester du contenu de phrasé, on
                reprend leurs classes sur des `span`. */}
            <span className="grid gap-1.5">
              <span className="font-semibold leading-none">Résultats bruts</span>
              <span className="text-muted-foreground text-sm">
                {reponsesList.length} réponses détaillées
              </span>
            </span>
            {brutOpen ? (
              <ChevronUp className="h-5 w-5 shrink-0 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-5 w-5 shrink-0 text-muted-foreground" />
            )}
          </button>
        </CardHeader>
        <CardContent
          id="egogramme-resultats-bruts"
          hidden={!brutOpen}
          className="overflow-x-auto"
        >
          <div className="space-y-6">
            {/* Résumé par profil */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Résumé par profil</h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {Object.entries(profilCounts).map(([profil, counts]) => (
                  <div
                    key={profil}
                    className="p-3 rounded-surface border border-trait text-encre"
                    style={{ backgroundColor: couleurProfilAtomique(profil) }}
                  >
                    <p className="text-sm font-medium">{profil}</p>
                    <div className="mt-2 flex items-center gap-2 text-sm">
                      <span className="flex items-center gap-1">
                        <CheckCircle className="h-3 w-3" />
                        {counts.oui}
                      </span>
                      <span className="flex items-center gap-1">
                        <XCircle className="h-3 w-3" />
                        {counts.non}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Liste des questions */}
            <div>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Réponses détaillées ({reponsesList.length} questions)
              </h3>
              <div className="space-y-2">
                {reponsesList.map(([key, reponse]) => (
                  <div
                    key={key}
                    className="p-3 border border-trait rounded-surface hover:bg-surface-2 flex items-center justify-between"
                  >
                    <div className="flex-1">
                      <p className="text-sm font-medium">{reponse.label}</p>
                      <p className="text-xs text-muted-foreground">
                        {reponse.name}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge
                        className="border-trait text-encre"
                        style={{
                          backgroundColor: couleurProfilAtomique(reponse.profil),
                        }}
                      >
                        {reponse.profil}
                      </Badge>
                      {reponse.response ? (
                        <div className="flex items-center gap-1 text-etat-fait">
                          <CheckCircle className="h-5 w-5" />
                          <span className="text-sm font-medium">Oui</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 text-etat-erreur">
                          <XCircle className="h-5 w-5" />
                          <span className="text-sm font-medium">Non</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
